import React, {useEffect, useState} from 'react';
import '../styles/TimeEntryPage.css';
import {getCurrentUserId} from '../utils/authHelper';
import StatisticsSidebar from '../components/StatisticsSidebar';
import {formatDate} from "../utils/dateConverter";
import {
    getTimeEntriesForUser,
    getTimeEntriesForUserConclusions,
    deleteTimeEntries,
    updateTimeEntriesForUser,
    addTimeEntries
} from "../services/TimeEntrieService";
import {
    getProjectsForUser,
    getAllUsers
} from "../services/UserService";

export default function TimeEntryPage() {
    const [projects, setProjects] = useState([]);
    const [, setUsers] = useState([]);
    const [entries, setEntries] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [stats, setStats] = useState(null);
    const [entryLimit, setEntryLimit] = useState(30);
    const [selectedProject, setSelectedProject] = useState('');
    const [formErrors, setFormErrors] = useState({});

    const getDefaultForm = () => {
        const now = new Date();
        const today = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const currentTime = now.toTimeString().slice(0, 5); // HH:MM

        return {
            userId: '',
            projectId: '',
            projectName: '',
            customerName: '',
            date: today,
            startTime: '',
            endTime: currentTime,
            breakMinutes: '0',
            travelMinutes: '0',
            workTime: '',
            kilometers: '0',
            location: 'OnSite',
            comment: '',
            activity: ''
        }
    }
    const [form, setForm] = useState(getDefaultForm());

    const filteredEntries = entries
        .filter(entry => !selectedProject || entry.projectId === selectedProject)
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, entryLimit);


    useEffect(() => {
        const init = async () => {
            await Promise.all([
                loadProjects(),
                loadUsers(),
                loadEntries(),
                loadStats(),
            ]);
        };
        void init();
    }, []);

    const loadProjects = async () => {
        const userId = getCurrentUserId();
        if (!userId) return alert("No userId found.");

        try {
            const res = await getProjectsForUser({userId})
            setProjects(res.projects);
        } catch {
            alert("Failed to load user projects")
        }
    }
    const loadUsers = async () => {
        try {
            const users = await getAllUsers();
            setUsers(users);
        } catch (err) {
            alert("Failed to load users")
        }
    }

    const loadStats = async () => {
        const userId = getCurrentUserId();
        if (!userId) return alert("No userId found.");

        try {
            const stats = await getTimeEntriesForUserConclusions({userId});
            setStats(stats);
        } catch (err) {
            alert("Failed to load statistics");
        }
    }

    const loadEntries = async () => {
        const userId = getCurrentUserId();
        if (!userId) return alert("No userId found.");

        try {
            const stats = await getTimeEntriesForUser({userId});
            setEntries(stats);
        } catch {
            alert("Failed to load time entries");
        }
    };

    const handleChange = e => {
        const {name, value} = e.target;
        setForm(prev => ({...prev, [name]: value}));
    };

    const handleDelete = async (id) => {
        if (window.confirm("Do you really want to delete this entry?")) {
            try {
                await deleteTimeEntries({id});
            } catch {
                alert("Failed to load time entries");
            }
        }
    };

    const handleSubmit = async () => {
        const userId = getCurrentUserId();
        if (!userId) return alert("No userId found.");

        if (!validateForm()) return;

        const payload = {
            userId: userId,
            projectId: form.projectId,
            date: form.date,
            startTime: form.startTime,
            endTime: form.endTime,
            breakMinutes: parseInt(form.breakMinutes),
            travelMinutes: parseInt(form.travelMinutes),
            kilometers: parseInt(form.kilometers),
            location: form.location,
            comment: form.comment,
            activity: form.activity
        };

        try {
            await addTimeEntries(payload);
            await loadEntries();
            resetForm();
            setShowForm(false);
        } catch (err) {
            console.error(err)
            alert("Failed adding new time entries");
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!form.projectId) errors.projectId = "Bitte ein Projekt auswählen.";
        if (!form.date) errors.date = "Datum ist erforderlich.";
        if (!form.startTime) errors.startTime = "Startzeit ist erforderlich.";
        if (!form.endTime) errors.endTime = "Endzeit ist erforderlich.";

        // Check time logic
        if (form.startTime && form.endTime && form.startTime >= form.endTime)
            errors.endTime = "Endzeit muss nach der Startzeit liegen.";

        // Numeric fields
        if (form.breakMinutes && isNaN(Number(form.breakMinutes)))
            errors.breakMinutes = "Pause muss eine Zahl sein.";
        if (form.travelMinutes && isNaN(Number(form.travelMinutes)))
            errors.travelMinutes = "Fahrtzeit muss eine Zahl sein.";
        if (form.kilometers && isNaN(Number(form.kilometers)))
            errors.kilometers = "Kilometer muss eine Zahl sein.";

        if (!form.activity.trim())
            errors.activity = "Bitte eine Aktivität angeben.";

        setFormErrors(errors);
        return Object.keys(errors).length === 0; // valid if no errors
    };

    const handleUpdate = async (entry) => {
        const userId = getCurrentUserId();
        if (!userId) return alert("No userId found.");

        const updatedEntry = {
            id: entry.id,
            userId: userId,
            projectId: entry.projectId,
            date: entry.date,
            startTime: entry.startTime,
            endTime: entry.endTime,
            breakMinutes: parseInt(entry.breakMinutes),
            travelMinutes: parseInt(entry.travelMinutes),
            kilometers: parseFloat(entry.kilometers),
            location: entry.location,
            comment: entry.comment,
            activity: entry.activity,
            workTime: entry.workTime ?? "00:00"
        };

        try {
            await updateTimeEntriesForUser({updatedEntry})
        } catch {
            alert("Failed to update time entries");
        }
    };


    const resetForm = () => {
        setForm(getDefaultForm());
    };

    const latestEntries = entries
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, entryLimit);

    return (
        <div className="main-container">
            <div className="sidebar_time_entry">
                <StatisticsSidebar stats={stats}/>
            </div>
            <div className="content_time_entry">
                <div className={`page-container ${selectedEntry ? 'sidebar-open' : ''}`}>

                    <div className="header-with-button">
                        <h2>Stunden</h2>
                        <button className="btn_new_entry" onClick={() => setShowForm(true)}>+</button>
                    </div>


                    {!showForm && !editingEntry && (
                        <div>
                            {latestEntries.length > 0 ? (
                                <div className="latest-entries">
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '20px',
                                        alignItems: 'flex-end',
                                        marginBottom: '20px'
                                    }}>
                                        <div className="form-group" style={{maxWidth: '200px'}}>
                                            <label>Anzahl der Einträge:</label>
                                            <input
                                                type="number"
                                                value={entryLimit}
                                                onChange={(e) => setEntryLimit(parseInt(e.target.value) || 1)}
                                                className="input"
                                                min="1"
                                                max="500"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Projekt Filter:</label>
                                            <select
                                                className="input"
                                                value={selectedProject}
                                                onChange={(e) => setSelectedProject(e.target.value)}
                                            >
                                                <option value="">Alle Projekte</option>
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <h3>Letzten Einträge:</h3>
                                    <table className="table">
                                        <thead>
                                        <tr>
                                            <th>Datum</th>
                                            <th>Wochentag</th>
                                            <th>Start</th>
                                            <th>Ende</th>
                                            <th>Pause</th>
                                            <th>Fahrtzeit</th>
                                            <th>Gesamt</th>
                                            <th>Projekt</th>
                                            <th>Tätigkeitsbeschreibung</th>
                                            <th>Location</th>
                                            <th>Aktion</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {filteredEntries.map(entry => (
                                            <tr key={entry.id}>

                                                <td>{formatDate(entry.date)}</td>
                                                <td>{entry.dayOfWeek}</td>
                                                <td>{entry.startTime}</td>
                                                <td>{entry.endTime}</td>
                                                <td>{entry.breakMinutes}</td>
                                                <td>{entry.travelMinutes}</td>
                                                <td><strong>{entry.workTime}</strong></td>
                                                <td>{entry.projectName}</td>
                                                <td>{entry.activity}</td>
                                                <td>{entry.location}</td>
                                                <td>
                                                    <button className="button-warning"
                                                            onClick={() => setEditingEntry(entry)}>Ändern
                                                    </button>
                                                    <button className="button-danger"
                                                            onClick={() => handleDelete(entry.id)}>Löschen
                                                    </button>
                                                    <button className="button-normal"
                                                            onClick={() => setSelectedEntry(entry)}>Beschreibung
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>

                                </div>
                            ) : (
                                <p>Keine Einträge gefunden.</p>
                            )}
                        </div>
                    )}

                    {selectedEntry && (
                        <div className="sidebar_details">
                            <h3>Details</h3>
                            <p><strong>Datum:</strong> {selectedEntry.date}</p>
                            <p><strong>Start:</strong> {selectedEntry.startTime}</p>
                            <p><strong>Ende:</strong> {selectedEntry.endTime}</p>
                            <p><strong>Pause:</strong> {selectedEntry.breakMinutes} Minuten</p>
                            <p><strong>Fahrtzeit:</strong> {selectedEntry.travelMinutes} Minuten</p>
                            <p><strong>KM:</strong> {selectedEntry.kilometers}</p>
                            <p><strong>Projekt:</strong> {selectedEntry.projectName}</p>
                            <p><strong>Tätigkeit:</strong> {selectedEntry.activity}</p>
                            <p><strong>Kommentar:</strong> {selectedEntry.comment}</p>
                            <p><strong>Arbeitszeit:</strong> {selectedEntry.workTime}</p>
                            <p><strong>Ort:</strong> {selectedEntry.location}</p>

                            <button className="button button-danger" onClick={() => setSelectedEntry(null)}>Schließen
                            </button>
                        </div>
                    )}

                    {editingEntry && (
                        <div className="form edit-form">
                            <h3>Eintrag Bearbeiten</h3>

                            <div className="form-group">
                                <label>Project:</label>
                                <select
                                    name="projectId"
                                    value={editingEntry.projectId}
                                    onChange={e => setEditingEntry({...editingEntry, projectId: e.target.value})}
                                    className="input"
                                >
                                    {projects.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Datum:</label>
                                <input type="date" name="date" value={editingEntry.date}
                                       onChange={e => setEditingEntry({...editingEntry, date: e.target.value})}
                                       className="input"/>
                            </div>

                            <div className="form-group">
                                <label>Start:</label>
                                <input type="time" name="startTime" value={editingEntry.startTime}
                                       onChange={e => setEditingEntry({...editingEntry, startTime: e.target.value})}
                                       className="input"/>
                            </div>

                            <div className="form-group">
                                <label>Ende:</label>
                                <input type="time" name="endTime" value={editingEntry.endTime}
                                       onChange={e => setEditingEntry({...editingEntry, endTime: e.target.value})}
                                       className="input"/>
                            </div>

                            <div className="form-group">
                                <label>Pause (Minuten):</label>
                                <input type="number" name="breakMinutes" value={editingEntry.breakMinutes}
                                       onChange={e => setEditingEntry({...editingEntry, breakMinutes: e.target.value})}
                                       className="input"/>
                            </div>

                            <div className="form-group">
                                <label>Reisezeit (Minuten):</label>
                                <input type="number" name="travelMinutes" value={editingEntry.travelMinutes}
                                       onChange={e => setEditingEntry({...editingEntry, travelMinutes: e.target.value})}
                                       className="input"/>
                            </div>

                            <div className="form-group">
                                <label>Kilometer:</label>
                                <input type="number" name="kilometers" value={editingEntry.kilometers}
                                       onChange={e => setEditingEntry({...editingEntry, kilometers: e.target.value})}
                                       className="input"/>
                            </div>


                            <div className="form-group">
                                <label>Kommentar:</label>
                                <input type="text" name="comment" value={editingEntry.comment}
                                       onChange={e => setEditingEntry({...editingEntry, comment: e.target.value})}
                                       className="input"/>
                            </div>

                            <div className="form-group">
                                <label>Aktivitätsbeschreibung:</label>
                                <input type="text" name="activity" value={editingEntry.activity}
                                       onChange={e => setEditingEntry({...editingEntry, activity: e.target.value})}
                                       className="input"/>
                            </div>

                            <div className="form-group">
                                <label>Einsatzort:</label>
                                <select name="location" value={editingEntry.location} onChange={handleChange}
                                        className="input">
                                    <option value="OnSite">OnSite</option>
                                    <option value="Remote">Remote</option>
                                    <option value="Office">Office</option>
                                    <option value="HomeOffice">HomeOffice</option>
                                </select>
                            </div>

                            <div className="form-buttons">
                                <button className="button" onClick={() => handleUpdate(editingEntry)}>💾 Speichern
                                </button>
                                <button className="button button-danger" onClick={() => setEditingEntry(null)}>❌ Löschen
                                </button>
                            </div>
                        </div>
                    )}


                    {showForm && (
                        <div className="form">
                            <h3>Neuer Eintrag</h3>

                            <div className="form-grid">

                                <div className="form-group">
                                    <label>Projekt:</label>
                                    <select name="projectId" value={form.projectId} onChange={handleChange}
                                            className="input">
                                        <option value="">-- Bitte Projekt wählen --</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                    {formErrors.projectId && <span className="error-text">{formErrors.projectId}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Datum:</label>
                                    <input type="date" name="date" value={form.date} onChange={handleChange}
                                           className={`input ${formErrors.date ? 'error' : ''}`}/>
                                    {formErrors.date && <span className="error-text">{formErrors.date}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Startzeit:</label>
                                    <input type="time" name="startTime" value={form.startTime} onChange={handleChange}
                                           className={`input ${formErrors.startTime ? 'error' : ''}`}/>
                                    {formErrors.startTime && <span className="error-text">{formErrors.startTime}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Endzeit:</label>
                                    <input type="time" name="endTime" value={form.endTime} onChange={handleChange}
                                           className={`input ${formErrors.endTime ? 'error' : ''}`}/>
                                    {formErrors.endTime && <span className="error-text">{formErrors.endTime}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Pause (Minuten):</label>
                                    <input type="number" name="breakMinutes" value={form.breakMinutes}
                                           onChange={handleChange}
                                           className={`input ${formErrors.breakMinutes ? 'error' : ''}`}/>
                                    {formErrors.breakMinutes && <span className="error-text">{formErrors.breakMinutes}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Fahrtzeit (Minuten):</label>
                                    <input type="number" name="travelMinutes" value={form.travelMinutes}
                                           onChange={handleChange}
                                           className={`input ${formErrors.travelMinutes ? 'error' : ''}`}/>
                                    {formErrors.travelMinutes && <span className="error-text">{formErrors.travelMinutes}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Kilometer:</label>
                                    <input type="number"
                                           name="kilometers" value={form.kilometers}
                                           onChange={handleChange}
                                           className={`input ${formErrors.kilometers ? 'error' : ''}`}/>
                                    {formErrors.kilometers && <span className="error-text">{formErrors.kilometers}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Aktivität:</label>
                                    <input type="text" name="activity" value={form.activity} onChange={handleChange}
                                           className={`input ${formErrors.activity ? 'error' : ''}`}/>
                                    {formErrors.activity && <span className="error-text">{formErrors.activity}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Einsatzort:</label>
                                    <select name="location" value={form.location} onChange={handleChange}
                                            className="input">
                                        <option value="OnSite">OnSite</option>
                                        <option value="Remote">Remote</option>
                                        <option value="Office">Office</option>
                                        <option value="HomeOffice">HomeOffice</option>
                                    </select>
                                    {formErrors.location && <span className="error-text">{formErrors.location}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Kommentar:</label>
                                    <input type="text" name="comment" value={form.comment} onChange={handleChange}
                                           className="input"/>
                                    {formErrors.comment && <span className="error-text">{formErrors.comment}</span>}
                                </div>

                                <div className="form-buttons">
                                    <button onClick={handleSubmit} className="button">💾 Speichern</button>
                                    <button onClick={() => {
                                        resetForm();
                                        setShowForm(false);
                                    }} className="button button-danger">❌ Abbrechen
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
